import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';

export default function ConfirmDialog({ 
  open, 
  onClose, 
  onCancel,
  onConfirm, 
  title, 
  message, 
  type = 'warning', // 'warning', 'danger', 'success', 'info'
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false
}) {
  const handleClose = (e) => {
    if (onClose) onClose(e);
    if (onCancel) onCancel(e);
  };

  const iconMap = {
    warning: <AlertTriangle className="h-12 w-12 text-yellow-500" />,
    danger: <XCircle className="h-12 w-12 text-red-500" />,
    success: <CheckCircle className="h-12 w-12 text-green-500" />,
    info: <Info className="h-12 w-12 text-blue-500" />
  };

  const buttonVariantMap = {
    warning: 'default',
    danger: 'destructive',
    success: 'default',
    info: 'default'
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            if (e.target.tagName === 'BUTTON') return;
            e.preventDefault();
            if (!loading && onConfirm) {
              onConfirm();
            }
          }
        }}
      >
        <DialogHeader className="items-center text-center">
          <div className="mx-auto mb-4">
            {iconMap[type]}
          </div>
          <DialogTitle className="text-xl">{title}</DialogTitle>
          <DialogDescription className="text-base mt-2">
            {message}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center gap-2 mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            className="min-w-[100px]"
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            variant={buttonVariantMap[type]}
            onClick={onConfirm}
            disabled={loading}
            className="min-w-[100px]"
          >
            {loading ? 'Processing...' : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Simple Alert Dialog (no confirmation needed)
export function AlertDialog({ 
  open, 
  onClose, 
  onCancel,
  title, 
  message, 
  type = 'info',
  buttonText = 'OK'
}) {
  const handleClose = (e) => {
    if (onClose) onClose(e);
    if (onCancel) onCancel(e);
  };

  const iconMap = {
    warning: <AlertTriangle className="h-12 w-12 text-yellow-500" />,
    danger: <XCircle className="h-12 w-12 text-red-500" />,
    success: <CheckCircle className="h-12 w-12 text-green-500" />,
    info: <Info className="h-12 w-12 text-blue-500" />
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="items-center text-center">
          <div className="mx-auto mb-4">
            {iconMap[type]}
          </div>
          <DialogTitle className="text-xl">{title}</DialogTitle>
          <DialogDescription className="text-base mt-2">
            {message}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center mt-4">
          <Button
            type="button"
            onClick={handleClose}
            className="min-w-[120px]"
          >
            {buttonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
